<template>
	<div>
		<div v-if="viewMode === 'table'" class="row">
			<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
				<div v-if="!items || !items.length" class="card no-action listItem orange">
					<div class="row content middle-sm middle-md middle-lg">
						<div class="col-xs-12 py-8 text-center">
							<div class="flex flex-col items-center gap-4">
								<div v-if="emptyStateConfig?.icon" class="text-4xl text-gray-400">
									<span :class="emptyStateConfig.icon"></span>
								</div>
								<div class="flex flex-col items-center gap-2">
									<p class="text-lg font-medium text-gray-600">
										{{ emptyStateConfig?.title || `No ${itemType} found` }}
									</p>
									<p v-if="emptyStateConfig?.description" class="text-sm text-gray-500">
										{{ emptyStateConfig.description }}
									</p>
								</div>
								<button
									v-if="emptyStateConfig?.action"
									class="jmiBtn orange withIcon"
									@click="emptyStateConfig.action.handler"
								>
									<span
										v-if="emptyStateConfig.action.icon"
										:class="emptyStateConfig.action.icon"
									></span>
									{{ emptyStateConfig.action.label }}
								</button>
							</div>
						</div>
					</div>
				</div>

				<div
					v-for="item in items"
					v-else
					:key="item.id"
					class="card listItem relative cursor-default"
					@click="handleItemClick($event, item)"
					@auxclick="handleItemClick($event, item)"
				>
					<div class="row content middle-sm middle-md middle-lg">
						<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12 tableOverflow">
							<div class="block sm:hidden">
								<slot name="mobile-view" :item="item">
									<div class="flex w-full flex-col">
										<div class="flex items-start justify-between">
											<div class="flex-grow">
												<div
													v-if="mobileLayout?.rightCorner"
													class="text-md block"
													:class="mobileLayout.rightCorner.class"
												>
													<p class="font-semibold uppercase text-gray-400">
														{{
															mobileLayout.rightCorner.label ||
															columns.find((c) => c.key === mobileLayout.rightCorner.key)
																?.label
														}}
													</p>
													<p class="truncate">
														{{
															formatColumnValue(item, {
																key: mobileLayout.rightCorner.key
															})
														}}
													</p>
												</div>

												<div class="mt-2 flex flex-col gap-2">
													<div v-if="mobileLayout?.header" class="flex flex-col">
														<p class="font-semibold uppercase text-gray-500">
															{{ mobileLayout.header.label }}:
														</p>
														<p>
															{{
																formatColumnValue(item, {
																	key: mobileLayout.header.key
																})
															}}
														</p>
													</div>

													<div
														v-for="field in mobileLayout?.mainFields"
														:key="field.key"
														class="flex flex-col"
														:class="field.class"
													>
														<p class="font-semibold uppercase text-gray-500">
															{{
																field.label ||
																columns.find((c) => c.key === field.key)?.label
															}}:
														</p>
														<p>{{ formatColumnValue(item, { key: field.key }) }}</p>
													</div>
												</div>
											</div>

											<div v-if="actions.length" class="ml-4" @click.stop>
												<dropdown-menu
													class="dropdownTextLeft dropdownMed"
													:items="getActionItems(item)"
												>
													<template #image>
														<button
															class="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
															aria-label="Actions"
														>
															<span class="fa-light fa-ellipsis-vertical"></span>
														</button>
													</template>
												</dropdown-menu>
											</div>
										</div>
									</div>
								</slot>
							</div>

							<div class="hidden sm:block">
								<div class="row middle-sm middle-md middle-lg">
									<template v-for="column in allowedColumns" :key="column.key">
										<div :class="column.class">
											<p
												@mouseover="
													column.tooltip &&
													createTooltip($event, getColumnValue(item, column))
												"
											>
												<slot :name="column.key" :item="item">
													{{ formatColumnValue(item, column) }}
												</slot>
											</p>
										</div>
									</template>
								</div>
							</div>
							<div
								v-if="actions.length"
								class="col-xs-12 col-sm-1 col-md-1 col-lg-1 end-sm end-md end-lg absolute bottom-4 right-2 sm:bottom-2 md:right-0"
								@click.stop
							>
								<dropdown-menu class="dropdownTextLeft dropdownMed" :items="getActionItems(item)">
									<template #image>
										<button
											class="flex h-6 w-full flex-row items-center justify-center rounded-sm bg-blue-500 px-4 text-white hover:bg-blue-600"
											style="color: white"
											aria-label="Action Button"
										>
											<span class="m-0 flex flex-row items-center justify-center">
												<p class="flex sm:hidden lg:flex">Action</p>
												<span class="fa-light fa-chevron-down ml-2 sm:m-0 lg:ml-2"></span>
											</span>
										</button>
									</template>
								</dropdown-menu>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div v-else class="flex w-full flex-wrap gap-4 px-2 pt-2">
			<slot name="card-view" :items="items">
				<div
					v-for="item in items"
					:key="item.id"
					class="basis-full md:basis-[calc(50%-0.5rem)] lg:basis-[calc(33%-0.5rem)] 2xl:basis-[calc(25%-0.75rem)]"
				>
					<div
						class="card detailCard flex cursor-pointer flex-col rounded-sm p-4 shadow-lg"
						@click="handleItemClick($event, item)"
						@auxclick="handleItemClick($event, item)"
					>
						<template v-for="column in allowedColumns" :key="column.key">
							<div class="mb-2">
								<p class="text-xs font-semibold uppercase text-gray-500">{{ column.label }}</p>
								<p>{{ formatColumnValue(item, column) }}</p>
							</div>
						</template>
					</div>
				</div>
			</slot>
		</div>
	</div>
</template>

<script>
export default {
	inject: ['userRoles', 'tenants'],
	props: {
		items: {
			type: Array,
			required: true
		},
		columns: {
			type: Array,
			required: true
		},
		itemType: {
			type: String,
			required: true
		},
		baseRoute: {
			type: String,
			required: true
		},
		canEdit: {
			type: Boolean,
			default: false
		},
		notClickable: {
			type: Boolean,
			default: false
		},
		viewMode: {
			type: String,
			default: 'table',
			validator: (value) => ['table', 'card'].includes(value)
		},
		mobileLayout: {
			type: Object,
			default: null
		},
		actions: {
			type: Array,
			default: () => []
		},
		emptyStateConfig: {
			type: Object,
			default: null
		}
	},
	computed: {
		getDefaultHeaderColumn() {
			const sortableColumn = this.columns.find((col) => col.sortable)
			return sortableColumn || this.columns[0]
		},
		allowedColumns() {
			return this.columns.filter((column) => {
				if (column.key === 'tenant') {
					return this.tenants?.length > 1 || this.$global.hasUserRole(this.userRoles, 'reseller')
				}
				return !column.permission || this.$global.hasUserRole(this.userRoles, column.permission)
			})
		},
		getDefaultMobileColumns() {
			return this.columns
				.filter((col) => {
					if (col.key === this.getDefaultHeaderColumn.key) return false
					if (col.class?.includes('hideTablet') || col.class?.includes('hideTabletLandscape')) return false
					if (col.hideMobile) return false
					return true
				})
				.slice(0, 3)
		}
	},
	methods: {
		handleItemClick(event, item) {
			if (window.getSelection().toString()) {
				return
			}
			if (this.notClickable) {
				return
			}
			let path
			if (this.itemType === 'porting') {
				path = `/porting/${item.id}`
			} else {
				path = this.canEdit ? `/${this.baseRoute}/${item.id}` : `/${this.baseRoute}`
			}
			if (event.type === 'auxclick' || event.shiftKey || event.metaKey || event.ctrlKey) {
				event.preventDefault()
				const url = this.$router.resolve(path).href
				window.open(url, '_blank', 'noopener,noreferrer')
				return
			}
			this.$router.push(path)
		},
		getColumnValue(item, column) {
			if (column.getValue) {
				return column.getValue(item)
			}
			return item[column.key]
		},
		formatColumnValue(item, column) {
			const value = this.getColumnValue(item, column)
			if (column.format) {
				return column.format(value)
			}
			if (column.key === 'createdAt' || column.key === 'updatedAt') {
				return this.$global.dateFormat(value)
			}
			return value || column.defaultValue || ''
		},
		createTooltip(event, text) {
			if (text) {
				event.target.setAttribute('title', text)
			}
		},
		getActionItems(item) {
			return this.actions
				.filter((action) => !action.show || action.show(item))
				.filter((action) => !action.permission || this.$global.hasUserRole(this.userRoles, action.permission))
				.map((action) => ({
					...action,
					handler: () => action.handler(item)
				}))
		}
	}
}
</script>
